import { Component } from '@theme/component';

/**
 * Dropdown component with Horizon-style delegated actions via on:click.
 */

class DropdownComponent extends Component {
  #trigger = /** @type {HTMLElement | null} */ (null);
  #panel = /** @type {HTMLElement | null} */ (null);

  #viewportGap = 8;
  #offset = 8;

  connectedCallback() {
    super.connectedCallback();

    this.#resolveElements();
    if (!this.#trigger || !this.#panel) return;

    if (!this.#panel.id) {
      this.#panel.id = `dropdown-${Math.random().toString(36).slice(2, 9)}`;
    }

    this.#panel.classList.toggle('_hidden', true);
    this.#trigger.setAttribute('aria-haspopup', 'menu');
    this.#trigger.setAttribute('aria-controls', this.#panel.id);
    this.#trigger.setAttribute('aria-expanded', 'false');

    document.addEventListener('click', this.#handleDocumentClick);
    this.addEventListener('keyup', this.#handleKeyUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('click', this.#handleDocumentClick);
    this.removeEventListener('keyup', this.#handleKeyUp);
    this.#removeViewportListeners();
  }

  /**
   * Toggle panel visibility.
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();
    if (!this.#panel) return;

    this.isOpen ? this.hide() : this.show();
  };

  /**
   * Closes dropdown panel.
   * Usage: on:click="/close"
   * @param {Event} [event]
   */
  close = (event) => {
    event?.preventDefault();
    this.hide();
  };

  get isOpen() {
    return this.classList.contains('is-open');
  }

  show = () => {
    if (!this.#panel || this.isOpen) return;
    this.#panel.classList.toggle('_hidden', false);
    this.classList.add('is-open');
    this.#positionPanel();
    this.#addViewportListeners();
    this.#trigger?.setAttribute('aria-expanded', 'true');
  };

  hide = () => {
    if (!this.#panel || !this.isOpen) return;
    this.#panel.classList.toggle('_hidden', true);
    this.classList.remove('is-open');
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.classList.remove('dropdown--drop-up', 'dropdown--resolved-left', 'dropdown--resolved-right');
    this.#removeViewportListeners();
  };

  #resolveElements() {
    this.#trigger = this.querySelector('.dropdown__trigger') ?? null;
    this.#panel = this.querySelector('.dropdown__panel') ?? null;
  }

  #handleDocumentClick = (event) => {
    if (!this.isOpen) return;
    if (!(event.target instanceof Node)) return;
    if (!this.contains(event.target)) {
      this.hide();
    }
  };

  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.hide();
      this.#trigger?.focus();
    }
  };

  #addViewportListeners() {
    window.addEventListener('resize', this.#positionPanel);
    window.addEventListener('scroll', this.#positionPanel, { passive: true });
  }

  #removeViewportListeners() {
    window.removeEventListener('resize', this.#positionPanel);
    window.removeEventListener('scroll', this.#positionPanel);
  }

  #positionPanel = () => {
    if (!this.#panel || !this.#trigger) return;
    if (!this.isOpen) return;

    this.#panel.style.setProperty('--dropdown-offset', `${this.#offset}px`);
    this.#panel.style.setProperty('--dropdown-shift-x', '0px');

    const prefersRight = this.classList.contains('dropdown--align-right');
    this.classList.toggle('dropdown--resolved-right', prefersRight);
    this.classList.toggle('dropdown--resolved-left', !prefersRight);
    this.classList.remove('dropdown--drop-up');

    let panelRect = this.#panel.getBoundingClientRect();

    const overflowsHorizontal =
      panelRect.left < this.#viewportGap || panelRect.right > window.innerWidth - this.#viewportGap;

    if (overflowsHorizontal) {
      this.classList.toggle('dropdown--resolved-right', !prefersRight);
      this.classList.toggle('dropdown--resolved-left', prefersRight);
      panelRect = this.#panel.getBoundingClientRect();
    }

    let shiftX = 0;
    if (panelRect.left < this.#viewportGap) {
      shiftX = this.#viewportGap - panelRect.left;
    } else if (panelRect.right > window.innerWidth - this.#viewportGap) {
      shiftX = window.innerWidth - this.#viewportGap - panelRect.right;
    }

    this.#panel.style.setProperty('--dropdown-shift-x', `${shiftX}px`);

    panelRect = this.#panel.getBoundingClientRect();
    const canDropUp = panelRect.height + this.#offset <= this.#trigger.getBoundingClientRect().top - this.#viewportGap;
    const shouldDropUp = panelRect.bottom > window.innerHeight - this.#viewportGap && canDropUp;
    this.classList.toggle('dropdown--drop-up', shouldDropUp);
  };
}

if (!customElements.get('dropdown-component')) {
  customElements.define('dropdown-component', DropdownComponent);
}
